/**
 * Inventory Service Layer
 * Handles API calls for inventory and inventory cards
 */

import type { InventoryCardWithCard } from '../../../server/src/inventory/type'
import type { InventoryCard, InventoryWithFullCards, StandardCondition, InventoryCardWithFullCard } from '../types/inventory'
import { getApiUrl } from '@/lib/utils'

const API_URL = getApiUrl()

function getFetchOptions(method: string, body?: unknown): RequestInit {
  const options: RequestInit = {
    method,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
  }
  if (body !== undefined) {
    options.body = JSON.stringify(body)
  }
  return options
}

async function handleResponse<T>(response: Response): Promise<T> {
  const data = await response.json().catch(() => ({}))
  if (!response.ok) {
    throw new Error((data as { error?: string }).error ?? `Request failed: ${response.status}`)
  }
  return data as T
}

function deserializeCard<T extends InventoryCard>(card: T): T {
  if (card.addedAt) {
    card.addedAt = new Date(card.addedAt)
  }
  return card
}
/**
 * Get the inventory for the logged-in user.
 * Creates a new inventory if the user has none.
 */
export async function getInventory(): Promise<InventoryWithFullCards> {
  const response = await fetch(`${API_URL}/api/inventory/`, getFetchOptions('GET'))
  const inventory: InventoryWithFullCards = await handleResponse<InventoryWithFullCards>(response)
  inventory.cards = inventory.cards.map((card: InventoryCardWithFullCard) => deserializeCard<InventoryCardWithCard>(card)) ?? []
  return inventory
}

export type AddCardInput = {
  cardId: string
  condition?: StandardCondition
  priceWhenAdded?: number
  storageLocationId?: string | null
}

/**
 * Add a card to the user's inventory.
 */
export async function addCard(input: AddCardInput): Promise<InventoryCard> {
  const response = await fetch(`${API_URL}/api/inventory/cards`, getFetchOptions('POST', input))
  const card = await handleResponse<InventoryCard>(response)
  return deserializeCard(card)
}

export type UpdateCardInput = {
  condition?: StandardCondition
  priceWhenAdded?: number | null
  storageLocationId?: string | null
}

/**
 * Update an inventory card.
 */
export async function updateCard(id: string, input: UpdateCardInput): Promise<InventoryCard> {
  const response = await fetch(`${API_URL}/api/inventory/cards/${id}`, getFetchOptions('PATCH', input))
  const card = await handleResponse<InventoryCard>(response)
  return deserializeCard(card)
}

/**
 * Delete an inventory card.
 */
export async function deleteCard(id: string): Promise<InventoryCard> {
  const response = await fetch(`${API_URL}/api/inventory/cards/${id}`, getFetchOptions('DELETE'))
  const card = await handleResponse<InventoryCard>(response)
  return deserializeCard(card)
}
